// Job 35: "Tariffs not appearing on site pages — network tariff codes are
// missing, appears in master data not under contracts to be used in comparison."
//
// Replaces DistributionConfigSection and the two inline tables SiteDetailPage
// used to build (the Network Tariffs assignment card and the tariff-period
// reference table). Those three surfaces described one site's network charges in
// three shapes, backed by three different stores — the codes on screen were not
// the code that got priced.
//
// This is one surface, modelled on RetailContractSection: a collapsible card per
// tariff code, its rate periods as columns, charge rows grouped down the side.
// Same tables (bv_contract + bv_contract_rate), same drawer pattern, so a
// validator reads network charges exactly the way they read retail ones.

import { Fragment, useState, useEffect, useCallback } from 'react';
import { Plus, ChevronDown, ChevronRight, ChevronsUpDown, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../../foundation/altzero_base/base/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../foundation/altzero_base/base/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../../../foundation/altzero_base/base/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../../../../foundation/altzero_base/base/components/ui/command';
import { Badge } from '../../../../foundation/altzero_base/base/components/ui/badge';
import { Switch } from '../../../../foundation/altzero_base/base/components/ui/switch';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { ContractDrawer } from './ContractDrawer';
import { TariffCodePicker } from './TariffCodePicker';
import { networkContractService, networkRateService } from '../../services/contractService';
import {
  CHARGE_UNIT_OPTIONS,
  NETWORK_RATE_ROWS,
  networkRateRowValue,
} from '../../utils/chargeNames';
import { tariffPeriodStatus } from '../../utils/networkTariffs';
import type {
  BvNetworkContract,
  BvNetworkContractFormData,
  BvNetworkRate,
  BvNetworkRateFormData,
  BvRateType,
  BvRateChargeUnit,
} from '../../types/billValidationTypes';

/** A catalog code the picker can be pointed at, with the name that goes with it. */
export type TariffOption = {
  tariff_code: string;
  tariff_name?: string | null;
  distributor?: string | null;
};

/**
 * One published bucket of a catalog tariff (`bv_site_network`, one row per
 * (org, distributor, code, bucket) since migration 060).
 *
 * Shown read-only beneath the rate periods. The first version of this card
 * dropped it, and a code with no hand-entered period then displayed nothing at
 * all — the distributor's own prices were on the site page before and stopped
 * being. They are NOT folded into the fixed rows above: the catalog quotes
 * Anytime Demand as `c/kW/day` while a network demand rate here is `$/kVA/year`,
 * and silently converting between the two is the mistake jobs 36 and 37 were.
 */
export type CatalogBucket = {
  id: string;
  tariff_code: string;
  name?: string | null;
  channel?: string | null;
  category?: string | null;
  price?: number | null;
  price_type?: string | null;
  open_to_new_applicants?: boolean | null;
};

type Props = {
  siteId: string;
  /** Catalog codes to pick from — the page already holds the full paged set. */
  tariffOptions?: TariffOption[];
  /** `bv_extn.network_distributor`, for the mismatch warning (job 35/50). */
  siteDistributor?: string | null;
  /**
   * Opens the page's "define a catalog tariff code" dialog (`bv_site_network`).
   *
   * Assigning a code to this site and defining what the distributor publishes
   * are two different acts, and this card only does the first. The trigger for
   * the second used to sit in the card job 35 replaced, so it is threaded back
   * in rather than left reachable only from Admin Portal.
   */
  onDefineCatalogCode?: () => void;
  /**
   * Opens the same dialog on an existing catalog code.
   *
   * Its trigger was the pencil on the old card's catalog rows. Kept reachable
   * because editing what the distributor publishes and editing this site's use
   * of it are different corrections, and only the second one is this card's.
   */
  onEditCatalogCode?: (tariffCode: string) => void;
  /** True while the catalog is still paging in, so the picker can say so. */
  catalogLoading?: boolean;
  /**
   * Every catalog bucket row, for the published-prices block. All rows, not one
   * per code: a tariff's prices ARE its buckets, so deduplicating by code would
   * show one of four.
   */
  catalogRows?: CatalogBucket[];
};

const EMPTY_CONTRACT: BvNetworkContractFormData = {
  // Network prices are republished each regulatory year, so a code a site holds
  // for several years has several periods. Smooth is the exception here, unlike
  // retail where a single flat term is the norm.
  rate_type: 'stepped',
  is_current: true,
  has_demand_contract: false,
};

const EMPTY_RATE: BvNetworkRateFormData = {
  // A network standing charge is per connection point per day. Same vocabulary
  // as the retail card's Supply row, so the two read alike.
  supply_unit: 'per_servicepoint_day',
};

const TODAY_ISO = new Date().toISOString().slice(0, 10);

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-AU') : '—';

const numberOrUndefined = (v: string) => (v === '' ? undefined : parseFloat(v));

/** A number field for one rate on the period drawer. */
function RateField({
  label, hint, step = '0.0001', value, onChange,
}: {
  label: string;
  hint?: string;
  step?: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <Input
        type="number"
        step={step}
        value={value ?? ''}
        onChange={(e) => onChange(numberOrUndefined(e.target.value))}
      />
      {hint && <p className="mt-0.5 text-[10px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function NetworkTariffSection({
  siteId, tariffOptions = [], siteDistributor,
  onDefineCatalogCode, onEditCatalogCode, catalogLoading = false,
  catalogRows = [],
}: Props) {
  const [contracts, setContracts] = useState<BvNetworkContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [contractDrawer, setContractDrawer] = useState(false);
  const [rateDrawer, setRateDrawer] = useState<{ open: boolean; contractId: string } | null>(null);
  const [editingContract, setEditingContract] = useState<BvNetworkContract | null>(null);
  const [editingRate, setEditingRate] = useState<BvNetworkRate | null>(null);
  const [contractForm, setContractForm] = useState<BvNetworkContractFormData>(EMPTY_CONTRACT);
  const [rateForm, setRateForm] = useState<BvNetworkRateFormData>(EMPTY_RATE);
  const [saving, setSaving] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await networkContractService.listBySite(siteId);
    if (r.success) {
      setContracts(r.data ?? []);
      setError(null);
    } else {
      setError(r.error ?? 'Failed to load network tariffs');
    }
    setLoading(false);
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  const setC = <K extends keyof BvNetworkContractFormData>(k: K, v: BvNetworkContractFormData[K]) =>
    setContractForm((f) => ({ ...f, [k]: v }));
  const setR = <K extends keyof BvNetworkRateFormData>(k: K, v: BvNetworkRateFormData[K]) =>
    setRateForm((f) => ({ ...f, [k]: v }));

  // ── Contract (one tariff code) ────────────────────────────────────────────

  // Two ways in, kept separate on purpose — they answer different questions.
  //
  // "Assign existing code…" is the common path: the code is already in the
  // catalog and the only new facts are the site's dates. Collapsing it into a
  // single Add button hid it behind a drawer, and picking a published code
  // stopped looking like a different act from inventing one.
  //
  // "New code" is the escape hatch for a code the catalog has not been loaded
  // with yet, or a one-off a distributor has not published.
  const openAssignExisting = (option: TariffOption) => {
    setEditingContract(null);
    setContractForm({
      ...EMPTY_CONTRACT,
      tariff_code: option.tariff_code,
      tariff_name: option.tariff_name ?? undefined,
      // The catalog's own distributor, falling back to the site's — assigning a
      // code from another distributor is legitimate but rare, and typing the
      // name every time invited the spelling drift that made every tariff read
      // as Ausgrid (job 50).
      distributor: option.distributor ?? siteDistributor ?? undefined,
    });
    setContractDrawer(true);
  };

  const openAddContract = () => {
    setEditingContract(null);
    setContractForm({ ...EMPTY_CONTRACT, distributor: siteDistributor ?? undefined });
    setContractDrawer(true);
  };

  const openEditContract = (c: BvNetworkContract) => {
    setEditingContract(c);
    setContractForm({
      tariff_code: c.tariff_code,
      tariff_name: c.tariff_name,
      distributor: c.distributor,
      valid_from: c.valid_from,
      valid_to: c.valid_to,
      rate_type: c.rate_type,
      has_demand_contract: c.has_demand_contract,
      is_current: c.is_current,
      notes: c.notes,
    });
    setContractDrawer(true);
  };

  const saveContract = async () => {
    setSaving(true);
    const r = editingContract
      ? await networkContractService.update(editingContract.id, contractForm)
      : await networkContractService.create(siteId, contractForm);
    if (r.success) {
      await load();
      setContractDrawer(false);
    } else {
      setError(r.error ?? 'Failed to save tariff');
    }
    setSaving(false);
  };

  const deleteContract = async (c: BvNetworkContract) => {
    if (!confirm(`Remove ${c.tariff_code} from this site, with all its rate periods?`)) return;
    const r = await networkContractService.delete(c.id);
    if (!r.success) setError(r.error ?? 'Failed to remove tariff');
    await load();
  };

  // ── Rate period ───────────────────────────────────────────────────────────

  const openAddRate = (contractId: string) => {
    setEditingRate(null);
    setRateForm(EMPTY_RATE);
    setRateDrawer({ open: true, contractId });
  };

  const openEditRate = (contractId: string, r: BvNetworkRate) => {
    setEditingRate(r);
    setRateForm({
      period_start: r.period_start,
      period_end: r.period_end,
      peak_rate: r.peak_rate,
      shoulder_rate: r.shoulder_rate,
      off_peak_rate: r.off_peak_rate,
      anytime_rate: r.anytime_rate,
      peak_demand_rate: r.peak_demand_rate,
      anytime_demand_rate: r.anytime_demand_rate,
      capacity_kw: r.capacity_kw,
      demand_kva: r.demand_kva,
      supply_charge: r.supply_charge,
      // Absent on load meant reopening a period and saving it wrote the unit
      // back as undefined even when one had been stored — the retail card had
      // exactly this bug.
      supply_unit: r.supply_unit ?? 'per_servicepoint_day',
    });
    setRateDrawer({ open: true, contractId });
  };

  const saveRate = async () => {
    if (!rateDrawer) return;
    setSaving(true);
    // On a smooth tariff the rates span the whole window, so the period dates
    // are hidden in the drawer — take the contract's own window instead.
    const parent = contracts.find((c) => c.id === rateDrawer.contractId);
    const payload: BvNetworkRateFormData =
      parent?.rate_type === 'smooth'
        ? { ...rateForm, period_start: parent.valid_from, period_end: parent.valid_to }
        : rateForm;
    const r = editingRate
      ? await networkRateService.update(editingRate.id, payload)
      : await networkRateService.create(rateDrawer.contractId, payload);
    if (r.success) {
      await load();
      setRateDrawer(null);
    } else {
      setError(r.error ?? 'Failed to save rate period');
    }
    setSaving(false);
  };

  const deleteRate = async (contractId: string, rateId: string) => {
    if (!confirm('Delete this rate period?')) return;
    const r = await networkRateService.delete(contractId, rateId);
    if (!r.success) setError(r.error ?? 'Failed to delete rate period');
    await load();
  };

  // The code being edited stays in the picker's list even when the catalog has
  // no such row — otherwise reopening a hand-entered code would show an empty
  // picker and saving would quietly blank the code it already had.
  const codeOptions: TariffOption[] =
    contractForm.tariff_code
    && !tariffOptions.some((o) => o.tariff_code === contractForm.tariff_code)
      ? [{ tariff_code: contractForm.tariff_code, tariff_name: contractForm.tariff_name }, ...tariffOptions]
      : tariffOptions;

  // Answered from the full catalog rather than the picker's options: those are
  // narrowed to the site's distributor, so a code legitimately assigned from
  // another distributor would have been labelled "Not in catalog" wrongly.
  const inCatalog = (code: string) => {
    const key = code.trim().toLowerCase();
    return catalogRows.some((b) => (b.tariff_code ?? '').trim().toLowerCase() === key)
      || tariffOptions.some((o) => (o.tariff_code ?? '').trim().toLowerCase() === key);
  };

  // One entry per code in the picker, not one per catalog bucket row — the page
  // narrows by distributor before passing them in, but a code published under
  // several buckets still arrives several times.
  const availableToAssign = tariffOptions.filter((o, i) =>
    tariffOptions.findIndex(
      (x) => (x.tariff_code ?? '').trim().toLowerCase() === (o.tariff_code ?? '').trim().toLowerCase(),
    ) === i);

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-gray-700">Network Tariffs</h3>
        <div className="flex items-center gap-2">
          <Popover open={assignOpen} onOpenChange={setAssignOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 w-56 justify-between text-xs font-normal">
                <span className="text-muted-foreground truncate">Assign existing code…</span>
                <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-0" align="end">
              <Command>
                <CommandInput placeholder="Search tariff codes…" className="h-9 text-xs" />
                <CommandList>
                  <CommandEmpty>
                    {catalogLoading
                      ? 'Loading tariff codes…'
                      : tariffOptions.length === 0
                        ? 'No tariff codes defined for your organisation.'
                        : 'No codes found.'}
                  </CommandEmpty>
                  <CommandGroup>
                    {/* Codes already assigned are NOT filtered out: a site can
                        legitimately come back onto a code it was on before, and
                        that second stint is a new window, not a duplicate. */}
                    {availableToAssign.map((o) => (
                      <CommandItem
                        key={`${o.tariff_code}-${o.distributor ?? ''}`}
                        value={`${o.tariff_code} ${o.tariff_name ?? ''}`}
                        onSelect={() => { openAssignExisting(o); setAssignOpen(false); }}
                      >
                        <span className="text-xs">
                          <span className="font-mono font-medium">{o.tariff_code}</span>
                          {o.tariff_name ? ` — ${o.tariff_name}` : ''}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={openAddContract}>
            <Plus className="h-3.5 w-3.5" /> New code
          </Button>
        </div>
      </div>

      {onDefineCatalogCode && (
        <p className="mb-2 text-xs text-gray-400">
          Code not in the list?{' '}
          <button
            type="button"
            className="text-blue-600 hover:underline"
            onClick={onDefineCatalogCode}
          >
            Define it in the tariff catalog
          </button>
          {' '}— or use New code to assign it to this site without a catalog entry.
        </p>
      )}

      {siteDistributor && (
        <p className="mb-2 text-xs text-gray-500">
          Site distributor: <span className="font-medium text-gray-700">{siteDistributor}</span>
        </p>
      )}

      {error && (
        <p className="mb-2 text-xs text-red-600">{error}</p>
      )}

      {loading ? (
        <p className="text-sm text-gray-400 py-2">Loading…</p>
      ) : contracts.length === 0 ? (
        <p className="text-sm text-gray-400 py-2">
          No network tariffs assigned to this site
        </p>
      ) : null}

      <div className="space-y-2">
        {contracts.map((c) => {
          const status = tariffPeriodStatus({ from: c.valid_from, to: c.valid_to }, TODAY_ISO);
          const rates = c.rates ?? [];
          // Matched tolerantly: the catalog is populated by bulk upload and by
          // hand, so the same code arrives as "EA302", " ea302 " and "Ea302".
          const buckets = catalogRows.filter(
            (b) => (b.tariff_code ?? '').trim().toLowerCase() === c.tariff_code.trim().toLowerCase(),
          );
          // Job 50: a code assigned against a different distributor than the
          // site's is the thing that used to be invisible.
          const distributorMismatch = !!siteDistributor && !!c.distributor
            && c.distributor.trim().toLowerCase() !== siteDistributor.trim().toLowerCase();
          return (
            <div key={c.id} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Tariff code header */}
              <div
                className="flex items-center justify-between px-3 py-2.5 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => setExpanded(expanded === c.id ? null : c.id)}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  {expanded === c.id ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                  <span className="text-sm font-mono font-semibold text-gray-800">
                    {c.tariff_code || 'No code'}
                  </span>
                  {c.tariff_name && (
                    <span className="text-sm text-gray-600">{c.tariff_name}</span>
                  )}
                  {c.distributor && (
                    <span className={cn('text-xs', distributorMismatch ? 'text-amber-700' : 'text-gray-400')}>
                      {c.distributor}
                      {distributorMismatch && ' — differs from site distributor'}
                    </span>
                  )}
                  <Badge variant="outline" className="text-xs capitalize">{c.rate_type}</Badge>
                  {/* A code assigned with a start date in the future saved
                      correctly but drew exactly like a live one, so scheduling
                      the next tariff looked impossible. */}
                  {status === 'current' && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-600 border-green-200">
                      Current
                    </Badge>
                  )}
                  {status === 'upcoming' && (
                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                      Upcoming
                    </Badge>
                  )}
                  {status === 'ended' && (
                    <Badge variant="outline" className="text-xs bg-gray-100 text-gray-600 border-gray-200">
                      Ended
                    </Badge>
                  )}
                  {c.has_demand_contract && (
                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                      Demand contract
                    </Badge>
                  )}
                  {/* Job 1: a code with no catalog definition used to vanish,
                      which read as "nothing assigned". It is named instead. */}
                  {c.tariff_code && tariffOptions.length > 0 && !inCatalog(c.tariff_code) && (
                    <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                      Not in catalog
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <span className="whitespace-nowrap">
                    {fmtDate(c.valid_from)} → {fmtDate(c.valid_to)}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); openEditContract(c); }}
                    title="Edit tariff assignment"
                    className="p-1 rounded hover:bg-white hover:text-gray-600"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteContract(c); }}
                    title="Remove from site"
                    className="p-1 rounded hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Rate periods */}
              {expanded === c.id && (
                <div className="p-3 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Rate Periods
                    </span>
                    <div className="flex items-center gap-2">
                      {/* Editing what the distributor publishes for this code —
                          a different correction from editing the site's use of
                          it, which is the pencil in the header above. */}
                      {onEditCatalogCode && c.tariff_code && inCatalog(c.tariff_code) && (
                        <button
                          type="button"
                          className="text-xs text-gray-400 hover:text-blue-600 hover:underline"
                          onClick={() => onEditCatalogCode(c.tariff_code)}
                        >
                          Edit catalog entry
                        </button>
                      )}
                      <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => openAddRate(c.id)}>
                        <Plus className="h-3 w-3 mr-1" /> Add Period
                      </Button>
                    </div>
                  </div>
                  {rates.length === 0 ? (
                    <p className="text-xs text-gray-400">
                      No rate periods entered — network charges for this code are priced
                      from the distributor&rsquo;s published prices below.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-gray-400 border-b border-gray-100">
                            <th className="pb-1.5 pr-4 whitespace-nowrap font-medium">Rate Type</th>
                            {rates.map((r) => {
                              // A smooth tariff holds no per-period dates — the
                              // rates span the whole window, so show the
                              // contract's own dates rather than an em dash.
                              const start = c.rate_type === 'smooth' ? c.valid_from : r.period_start;
                              const end = c.rate_type === 'smooth' ? c.valid_to : r.period_end;
                              return (
                                <th key={r.id} className="pb-1.5 pr-3 whitespace-nowrap font-medium">
                                  <div className="flex items-center gap-1.5">
                                    <span>{fmtDate(start)} → {fmtDate(end)}</span>
                                    <div className="flex items-center gap-0.5">
                                      <button
                                        onClick={() => openEditRate(c.id, r)}
                                        title="Edit rate period"
                                        className="p-0.5 hover:text-blue-600"
                                      >
                                        <Pencil className="h-3 w-3" />
                                      </button>
                                      <button
                                        onClick={() => deleteRate(c.id, r.id)}
                                        title="Delete rate period"
                                        className="p-0.5 hover:text-red-600"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </button>
                                    </div>
                                  </div>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {NETWORK_RATE_ROWS.map((row, i) => (
                            <Fragment key={row.label}>
                              {NETWORK_RATE_ROWS[i - 1]?.group !== row.group && (
                                <tr>
                                  <td
                                    colSpan={1 + rates.length}
                                    className="pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400"
                                  >
                                    {row.group}
                                  </td>
                                </tr>
                              )}
                              <tr className="hover:bg-gray-50">
                                <td className="py-1.5 pr-4 text-gray-500 font-medium whitespace-nowrap">
                                  {row.label}
                                </td>
                                {rates.map((r) => (
                                  <td key={r.id} className="py-1.5 pr-3 whitespace-nowrap">
                                    {networkRateRowValue(row, r)}
                                  </td>
                                ))}
                              </tr>
                            </Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* ── Published prices, from the tariff catalog ───────────
                      Read-only, and stated in the catalog's own units. This is
                      what the site page showed before the card was rebuilt, and
                      what the NEM pipeline actually prices network usage from —
                      the rate periods above only take over as a fallback when
                      the pipeline has produced nothing for the period. */}
                  {buckets.length > 0 && (
                    <div className="mt-4">
                      <p className="mb-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Published Prices{' '}
                        <span className="normal-case font-normal text-gray-400">
                          — from the tariff catalog, read-only
                        </span>
                      </p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-gray-400 border-b border-gray-100">
                              <th className="pb-1.5 pr-3 whitespace-nowrap font-medium">Name</th>
                              <th className="pb-1.5 pr-3 whitespace-nowrap font-medium">Channel</th>
                              <th className="pb-1.5 pr-3 whitespace-nowrap font-medium">Category</th>
                              <th className="pb-1.5 pr-3 whitespace-nowrap font-medium text-right">Price</th>
                              <th className="pb-1.5 pr-3 whitespace-nowrap font-medium">Unit</th>
                              <th className="pb-1.5 whitespace-nowrap font-medium">Open to new</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {buckets.map((b) => (
                              <tr key={b.id} className="hover:bg-gray-50">
                                <td className="py-1.5 pr-3 text-gray-700 whitespace-nowrap">{b.name ?? '—'}</td>
                                <td className="py-1.5 pr-3 text-muted-foreground whitespace-nowrap">{b.channel ?? '—'}</td>
                                <td className="py-1.5 pr-3 text-muted-foreground whitespace-nowrap">{b.category ?? '—'}</td>
                                <td className="py-1.5 pr-3 text-right tabular-nums whitespace-nowrap">{b.price ?? '—'}</td>
                                {/* The unit the DEFINITION holds, verbatim —
                                    c/kWh, c/kW/day, $/day. Not normalised to
                                    the fixed rows' units above, because those
                                    are different units for different facts. */}
                                <td className="py-1.5 pr-3 text-muted-foreground whitespace-nowrap">{b.price_type ?? '—'}</td>
                                <td className="py-1.5 text-muted-foreground whitespace-nowrap">
                                  {b.open_to_new_applicants === false ? 'No' : 'Yes'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {buckets.length === 0 && c.tariff_code && (
                    <p className="mt-4 text-xs text-amber-700">
                      No published prices found for{' '}
                      <span className="font-mono font-semibold">{c.tariff_code}</span> — this
                      code has no catalog definition, so network usage cannot be priced from
                      the distributor&rsquo;s rates. Enter a rate period above, or define the
                      code in the catalog.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Tariff code drawer — assign an existing code or type a new one */}
      <ContractDrawer
        open={contractDrawer}
        onClose={() => setContractDrawer(false)}
        title={editingContract ? 'Edit Network Tariff' : 'Add Network Tariff'}
      >
        <div className="space-y-4">
          {/* Searchable but still a text field: a code the catalog has not been
              loaded with yet, or a one-off a distributor has not published, has
              to remain enterable by hand. Picking from the list fills the name. */}
          <TariffCodePicker
            code={contractForm.tariff_code ?? ''}
            name={contractForm.tariff_name ?? ''}
            options={codeOptions}
            onChange={(tariff_code, tariff_name) => {
              setContractForm((f) => ({
                ...f,
                tariff_code,
                tariff_name,
                // Pick up the catalog's distributor with the code, so the
                // mismatch warning has something true to compare against.
                distributor:
                  codeOptions.find((o) => o.tariff_code === tariff_code)?.distributor
                  ?? f.distributor,
              }));
            }}
          />

          <div>
            <Label>Distributor</Label>
            <Input
              className="mt-1"
              placeholder="e.g. Ausgrid"
              value={contractForm.distributor ?? ''}
              onChange={(e) => setC('distributor', e.target.value)}
            />
          </div>

          {/* The site's window on this code — NOT the distributor's price window
              (bv_site_network.start_date/end_date), and not the distributor's own
              validity. Those date different things and the three used to be
              confused for one another. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                className="mt-1"
                max={contractForm.valid_to ?? undefined}
                value={contractForm.valid_from ?? ''}
                onChange={(e) => setC('valid_from', e.target.value || undefined)}
              />
              <p className="mt-0.5 text-[10px] text-muted-foreground">
                When this site moved onto the code
              </p>
            </div>
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                className="mt-1"
                min={contractForm.valid_from ?? undefined}
                value={contractForm.valid_to ?? ''}
                onChange={(e) => setC('valid_to', e.target.value || undefined)}
              />
              <p className="mt-0.5 text-[10px] text-muted-foreground">
                Leave blank while the site is still on it
              </p>
            </div>
          </div>

          <div>
            <Label>Rate Type</Label>
            <Select
              value={contractForm.rate_type}
              onValueChange={(v) => setC('rate_type', v as BvRateType)}
            >
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="stepped">Stepped — rates change per period</SelectItem>
                <SelectItem value="smooth">Smooth — one set of rates for the term</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
            <div>
              <Label className="cursor-pointer">Demand contract</Label>
              {/* A demand CONTRACT is not the same fact as a demand quantity —
                  Jemena sites carry the flag with no kVA recorded — so it is its
                  own switch rather than inferred from the kVA figure. */}
              <p className="text-[10px] text-muted-foreground">
                Site has a contracted demand with the distributor
              </p>
            </div>
            <Switch
              checked={contractForm.has_demand_contract ?? false}
              onCheckedChange={(v) => setC('has_demand_contract', v)}
            />
          </div>

          <div>
            <Label>Notes</Label>
            <Input
              className="mt-1"
              value={contractForm.notes ?? ''}
              onChange={(e) => setC('notes', e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setContractDrawer(false)}>Cancel</Button>
            <Button
              onClick={saveContract}
              disabled={saving || !contractForm.tariff_code?.trim()}
            >
              {saving ? 'Saving…' : editingContract ? 'Save Changes' : 'Add Tariff'}
            </Button>
          </div>
        </div>
      </ContractDrawer>

      {/* Rate period drawer */}
      <ContractDrawer
        open={!!rateDrawer?.open}
        onClose={() => setRateDrawer(null)}
        title={editingRate ? 'Edit Rate Period' : 'Add Rate Period'}
      >
        <div className="space-y-4">
          {contracts.find((c) => c.id === rateDrawer?.contractId)?.rate_type === 'stepped' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Period Start</Label>
                <Input
                  type="date"
                  className="mt-1"
                  max={rateForm.period_end ?? undefined}
                  value={rateForm.period_start ?? ''}
                  onChange={(e) => setR('period_start', e.target.value || undefined)}
                />
              </div>
              <div>
                <Label>Period End</Label>
                <Input
                  type="date"
                  className="mt-1"
                  min={rateForm.period_start ?? undefined}
                  value={rateForm.period_end ?? ''}
                  onChange={(e) => setR('period_end', e.target.value || undefined)}
                />
              </div>
            </div>
          )}

          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              Network Usage (¢/kWh)
            </p>
            <div className="grid grid-cols-2 gap-3">
              <RateField label="Peak" value={rateForm.peak_rate} onChange={(v) => setR('peak_rate', v)} />
              <RateField label="Shoulder" value={rateForm.shoulder_rate} onChange={(v) => setR('shoulder_rate', v)} />
              <RateField label="Off-Peak" value={rateForm.off_peak_rate} onChange={(v) => setR('off_peak_rate', v)} />
              <RateField label="Anytime" value={rateForm.anytime_rate} onChange={(v) => setR('anytime_rate', v)} />
            </div>
          </div>

          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              Demand
            </p>
            <div className="grid grid-cols-2 gap-3">
              <RateField
                label="Peak Demand"
                hint="$/kVA/year"
                value={rateForm.peak_demand_rate}
                onChange={(v) => setR('peak_demand_rate', v)}
              />
              <RateField
                label="Anytime Demand"
                hint="$/kVA/year"
                value={rateForm.anytime_demand_rate}
                onChange={(v) => setR('anytime_demand_rate', v)}
              />
              <RateField
                label="Capacity"
                hint="kW contracted"
                step="0.001"
                value={rateForm.capacity_kw}
                onChange={(v) => setR('capacity_kw', v)}
              />
              <RateField
                label="Contracted Demand"
                hint="kVA"
                step="0.001"
                value={rateForm.demand_kva}
                onChange={(v) => setR('demand_kva', v)}
              />
            </div>
          </div>

          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              Network Supply
            </p>
            {/* Amount and unit together — entering one without the other is what
                left the rates table showing "$5" with no indication of per what. */}
            <div className="grid grid-cols-2 gap-3">
              <RateField
                label="Standing Charge"
                step="0.000001"
                value={rateForm.supply_charge}
                onChange={(v) => setR('supply_charge', v)}
              />
              <div>
                <Label>Unit</Label>
                <Select
                  value={rateForm.supply_unit ?? 'per_servicepoint_day'}
                  onValueChange={(v) => setR('supply_unit', v as BvRateChargeUnit)}
                >
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CHARGE_UNIT_OPTIONS.map((u) => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setRateDrawer(null)}>Cancel</Button>
            <Button onClick={saveRate} disabled={saving}>
              {saving ? 'Saving…' : editingRate ? 'Save Changes' : 'Add Period'}
            </Button>
          </div>
        </div>
      </ContractDrawer>
    </div>
  );
}
